/**
 * The public-funnel journey id: one UUID that ties a visitor's steps from the
 * marketing site through signup together (port of apps/web
 * src/analytics/publicFunnel.ts). The landing site hands it over as
 * `?funnel_journey_id=`; it lives in sessionStorage for the rest of the tab.
 *
 * It is attached to every analytics event (`journey_id`) and sent with signup
 * (`funnel_journey_id`), where the API records the server-side conversion.
 */
import { readStored, writeStored } from './storage';

const STORAGE_KEY = 'lemon_public_funnel_journey_v1';
export const FUNNEL_JOURNEY_PARAM = 'funnel_journey_id';

const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Only a well-formed UUIDv4 is accepted: the value is forwarded to the API and PostHog. */
export const isJourneyId = (value: unknown): value is string => typeof value === 'string' && UUID_V4.test(value);

/** Used only when sessionStorage is unavailable, so the id is at least stable for this page. */
let volatileJourneyId: string | null = null;

const remember = (id: string): string => {
  writeStored(STORAGE_KEY, id, 'session');
  volatileJourneyId = id;
  return id;
};

/**
 * Take `?funnel_journey_id=` off the address bar, adopting it when it is a
 * valid UUIDv4. Runs once at boot, before the router reads the URL, so the
 * router never sees (or re-writes) the parameter. The parameter is removed
 * even when invalid: nothing else reads it.
 */
export const adoptFunnelJourneyIdFromUrl = (): void => {
  let url: URL;
  try {
    url = new URL(window.location.href);
  } catch {
    return;
  }
  if (!url.searchParams.has(FUNNEL_JOURNEY_PARAM)) return;
  const incoming = url.searchParams.get(FUNNEL_JOURNEY_PARAM);
  if (isJourneyId(incoming)) remember(incoming);
  url.searchParams.delete(FUNNEL_JOURNEY_PARAM);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
};

/** This tab's journey id: the adopted one, the stored one, or a new one. */
export const funnelJourneyId = (): string => {
  const stored = readStored(STORAGE_KEY, 'session');
  if (isJourneyId(stored)) return (volatileJourneyId = stored);
  if (volatileJourneyId) return remember(volatileJourneyId);
  return remember(crypto.randomUUID());
};
